import axios, { AxiosError } from 'axios'
import { API_BASE } from './api'
import { getIdToken } from '@/utils/auth'

export interface PromoCode {
  code: string
  remainingUses: number
  expirationDate: string // ISO string when returned from the API
  id?: string
}

export interface PromoCodeValidationResult {
  isValid: boolean
  error?: string
  promoCode?: PromoCode
}

/**
 * Validates a promo code via the backend REST endpoint.
 * No state change — call redeemPromoCode to consume a use.
 *
 * Rate limiting, auth, and Firestore access are handled server-side.
 */
export const validatePromoCode = async (code: string): Promise<PromoCodeValidationResult> => {
  try {
    const { data } = await axios.post<PromoCodeValidationResult>(
      `${API_BASE}/promoCodeApi/v1/validate`,
      { code }
    )
    return data
  } catch (error) {
    const axiosError = error as AxiosError<{ error?: string; isValid?: boolean }>
    // Rate limit or server-side rejection still comes back as a structured response
    if (axiosError.response?.data?.error) {
      return {
        isValid: false,
        error: axiosError.response.data.error,
      }
    }
    console.error('Error validating promo code:', error)
    return {
      isValid: false,
      error: 'Error validating promo code',
    }
  }
}

/**
 * Consumes one use of a valid promo code via the backend REST endpoint.
 * Called during signup after successful registration.
 *
 * Throws if the code is invalid, expired, or fully used.
 */
export const decrementPromoCodeUses = async (code: string): Promise<void> => {
  try {
    const token = await getIdToken()
    await axios.post(
      `${API_BASE}/promoCodeApi/v1/redeem`,
      { code },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    )
  } catch (error) {
    const axiosError = error as AxiosError<{ error?: string }>
    const message = axiosError.response?.data?.error ?? 'Failed to use promo code'
    console.error('Error using promo code:', message)
    throw new Error(message)
  }
}
